// Die Formularblätter. Jedes hält seinen Entwurf lokal und schickt ihn erst
// beim Speichern weg — so bleibt eine abgebrochene Eingabe folgenlos.

import { useState } from "react";
import {
  Sheet, TextFeld, ZahlFeld, WahlFeld, TextflaecheFeld, HakenFeld, ausRecord, ausListe,
} from "./Bausteine.tsx";
import { api } from "../api.ts";
import { AnhangFeld, type Vorgemerkt } from "./Anhang.tsx";
import { alsFeldDatum } from "../formate.ts";
import { useZustand } from "../zustand.tsx";
import {
  KOSTENTRAEGER, MANGEL_ARTEN, NEU_TEXT, RAEUME, TYPEN,
  BESTAND_TEXT, POSTEN_TEXT, PRIO_TEXT, STATUS_TEXT, ZUSTAND_TEXT,
  type Einheit, type InventarPosition, type Mangel, type Mietvertrag,
  type Objekt, type Sanierungsposten, type VorlagePosition,
} from "../typen.ts";

/** Leere Zahlfelder sollen als 0 ankommen, nicht als leerer String. */
const zahl = (wert: string): number => {
  const n = Number(wert.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
/** Ein leeres Datumsfeld ist kein Datum, sondern null. */
const datumOderNull = (wert: string): string | null => wert || null;

const AbbrechenSpeichern = (
  { aufSchliessen, laeuft }: { aufSchliessen: () => void; laeuft: boolean },
) => (
  <div className="r">
    <button className="btn btn-quiet" type="button" onClick={aufSchliessen}>Abbrechen</button>
    <button className="btn btn-primary" type="submit" disabled={laeuft}>
      {laeuft ? "Speichert …" : "Speichern"}
    </button>
  </div>
);

/** Nimmt den Speichervorgang ab: Zustand, Fehlerbehandlung, Schließen. */
function useSpeichern(aufSchliessen: () => void) {
  const { aendern } = useZustand();
  const [laeuft, setzeLaeuft] = useState(false);
  return {
    laeuft,
    async fuehreAus(aktion: () => Promise<import("../typen.ts").Portfolio>, text?: string) {
      setzeLaeuft(true);
      const ok = await aendern(aktion, text);
      setzeLaeuft(false);
      if (ok) aufSchliessen();
    },
  };
}

// ------------------------------------------------------------------ Objekt

export function ObjektBlatt(
  { objekt, aufSchliessen }: { objekt: Objekt | null; aufSchliessen: () => void },
) {
  const { laeuft, fuehreAus } = useSpeichern(aufSchliessen);
  const [f, setzeF] = useState({
    name: objekt?.name ?? "",
    strasse: objekt?.strasse ?? "",
    plz: objekt?.plz ?? "",
    ort: objekt?.ort ?? "",
    kaufdatum: alsFeldDatum(objekt?.kaufdatum),
    kaufpreis: String(objekt?.kaufpreis ?? ""),
    nebenkosten: String(objekt?.nebenkosten ?? ""),
    notiz: objekt?.notiz ?? "",
  });
  const setze = (feld: keyof typeof f) => (w: string) => setzeF({ ...f, [feld]: w });

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!f.name.trim()) return;
      const daten = {
        name: f.name.trim(), strasse: f.strasse, plz: f.plz, ort: f.ort,
        kaufdatum: datumOderNull(f.kaufdatum),
        kaufpreis: zahl(f.kaufpreis), nebenkosten: zahl(f.nebenkosten), notiz: f.notiz,
      };
      void fuehreAus(
        () => objekt ? api.objektAendern(objekt.id, daten) : api.objektAnlegen(daten),
        objekt ? "Gespeichert" : "Objekt angelegt");
    }}>
      <Sheet
        titel={objekt ? "Objekt bearbeiten" : "Neues Objekt"}
        aufSchliessen={aufSchliessen}
        fuss={<AbbrechenSpeichern aufSchliessen={aufSchliessen} laeuft={laeuft} />}
      >
        <div className="form-grid">
          <TextFeld titel="Bezeichnung *" voll wert={f.name} aendern={setze("name")}
            platzhalter="z. B. MFH Musterstraße 12" />
          <TextFeld titel="Straße und Hausnummer" voll wert={f.strasse} aendern={setze("strasse")} />
          <TextFeld titel="PLZ" wert={f.plz} aendern={setze("plz")} />
          <TextFeld titel="Ort" wert={f.ort} aendern={setze("ort")} />
          <TextFeld titel="Kaufdatum" typ="date" wert={f.kaufdatum} aendern={setze("kaufdatum")} />
          <ZahlFeld titel="Kaufpreis (€)" wert={f.kaufpreis} aendern={setze("kaufpreis")} platzhalter="0" />
          <ZahlFeld titel="Kaufnebenkosten (€)" wert={f.nebenkosten} aendern={setze("nebenkosten")}
            platzhalter="GrESt, Notar, Makler …" />
          <TextflaecheFeld titel="Notizen" wert={f.notiz} aendern={setze("notiz")} />
        </div>
      </Sheet>
    </form>
  );
}

// ----------------------------------------------------------------- Einheit

export function EinheitBlatt(
  { objektId, einheit, aufSchliessen }:
  { objektId: string; einheit: Einheit | null; aufSchliessen: () => void },
) {
  const { laeuft, fuehreAus } = useSpeichern(aufSchliessen);
  const { bestand } = useZustand();
  const [f, setzeF] = useState({
    bezeichnung: einheit?.bezeichnung ?? "",
    typ: einheit?.typ ?? "Wohnung",
    flaeche: einheit?.flaeche === null || einheit?.flaeche === undefined ? "" : String(einheit.flaeche),
    zimmer: einheit?.zimmer === null || einheit?.zimmer === undefined ? "" : String(einheit.zimmer),
    status: einheit?.status ?? "sanierung",
    moebliert: einheit?.moebliert ?? false,
    zielmiete: String(einheit?.zielmiete ?? ""),
    vorlageUebernehmen: true,
  });
  const setze = <K extends keyof typeof f>(feld: K) => (w: typeof f[K]) => setzeF({ ...f, [feld]: w });

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!f.bezeichnung.trim()) return;
      const daten = {
        bezeichnung: f.bezeichnung.trim(), typ: f.typ,
        flaeche: f.flaeche === "" ? null : zahl(f.flaeche),
        zimmer: f.zimmer === "" ? null : zahl(f.zimmer),
        status: f.status, moebliert: f.moebliert, zielmiete: zahl(f.zielmiete),
      };
      void fuehreAus(
        () => einheit
          ? api.einheitAendern(einheit.id, daten)
          : api.einheitAnlegen(objektId, { ...daten, vorlageUebernehmen: f.vorlageUebernehmen }),
        einheit ? "Gespeichert" : "Einheit angelegt");
    }}>
      <Sheet
        titel={einheit ? "Einheit bearbeiten" : "Neue Einheit"}
        aufSchliessen={aufSchliessen}
        fuss={<AbbrechenSpeichern aufSchliessen={aufSchliessen} laeuft={laeuft} />}
      >
        <div className="form-grid">
          <TextFeld titel="Bezeichnung *" voll wert={f.bezeichnung} aendern={setze("bezeichnung")}
            platzhalter="z. B. WE 1, EG links" />
          <WahlFeld titel="Typ" wert={f.typ} aendern={setze("typ")} optionen={ausListe(TYPEN)} />
          <ZahlFeld titel="Fläche (m²)" wert={f.flaeche} aendern={setze("flaeche")} platzhalter="0" />
          <WahlFeld titel="Status" wert={f.status} aendern={setze("status")}
            optionen={ausRecord(STATUS_TEXT)} />
          <ZahlFeld titel="Zimmer" wert={f.zimmer} aendern={setze("zimmer")}
            platzhalter="Umlageschlüssel Nebenkosten" />
          <ZahlFeld titel="Zielmiete kalt (€/Monat)" voll wert={f.zielmiete} aendern={setze("zielmiete")}
            platzhalter="für die Potenzialrechnung" />
          <HakenFeld titel="Möblierte Vermietung geplant oder aktiv"
            wert={f.moebliert} aendern={setze("moebliert")} />
          {einheit ? null : (
            <HakenFeld
              titel={`Standard-Inventarliste übernehmen (${bestand?.vorlage.length ?? 0} Positionen)`}
              wert={f.vorlageUebernehmen} aendern={setze("vorlageUebernehmen")} />
          )}
        </div>
      </Sheet>
    </form>
  );
}

// ------------------------------------------------------------- Mietvertrag

export function VertragBlatt(
  { einheit, aufSchliessen }: { einheit: Einheit; aufSchliessen: () => void },
) {
  const { laeuft, fuehreAus } = useSpeichern(aufSchliessen);
  const { aendern } = useZustand();
  const vertrag: Mietvertrag | null = einheit.mietvertrag;
  const [f, setzeF] = useState({
    mieter_name: vertrag?.mieter_name ?? "",
    kontakt: vertrag?.kontakt ?? "",
    kaltmiete: vertrag ? String(Number(vertrag.kaltmiete)) : "",
    nebenkosten_vz: vertrag ? String(Number(vertrag.nebenkosten_vz)) : "",
    kaution: vertrag ? String(Number(vertrag.kaution)) : "",
    personen: vertrag?.personen === null || vertrag?.personen === undefined ? "" : String(vertrag.personen),
    beginn: alsFeldDatum(vertrag?.beginn),
  });
  const setze = (feld: keyof typeof f) => (w: string) => setzeF({ ...f, [feld]: w });

  const kalt = zahl(f.kaltmiete);
  const warm = kalt + zahl(f.nebenkosten_vz);

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!f.mieter_name.trim() || kalt <= 0) return;
      const daten = {
        mieter_name: f.mieter_name.trim(), kontakt: f.kontakt,
        kaltmiete: kalt, nebenkosten_vz: zahl(f.nebenkosten_vz), kaution: zahl(f.kaution),
        personen: f.personen === "" ? null : Math.round(zahl(f.personen)),
        beginn: datumOderNull(f.beginn),
      };
      void fuehreAus(
        () => vertrag ? api.vertragAendern(vertrag.id, daten) : api.vertragAnlegen(einheit.id, daten),
        vertrag ? "Gespeichert" : "Mietvertrag angelegt");
    }}>
      <Sheet
        titel={`Mietvertrag — ${einheit.bezeichnung}`}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {vertrag ? (
            <button className="btn btn-danger" type="button" onClick={() => {
              if (!confirm("Vertrag zum heutigen Tag beenden? Er bleibt als Historie erhalten.")) return;
              void aendern(() => api.vertragBeenden(vertrag.id), "Vertrag beendet").then(aufSchliessen);
            }}>Vertrag beenden</button>
          ) : <span />}
          <AbbrechenSpeichern aufSchliessen={aufSchliessen} laeuft={laeuft} />
        </>}
      >
        <div className="form-grid">
          <TextFeld titel="Mieter *" voll wert={f.mieter_name} aendern={setze("mieter_name")}
            platzhalter="Name" />
          <TextFeld titel="Kontakt" voll wert={f.kontakt} aendern={setze("kontakt")}
            platzhalter="Telefon oder E-Mail" />
          <ZahlFeld titel="Kaltmiete (€/Monat) *" wert={f.kaltmiete} aendern={setze("kaltmiete")} />
          <ZahlFeld titel="NK-Vorauszahlung (€/Monat)" wert={f.nebenkosten_vz}
            aendern={setze("nebenkosten_vz")} />
          <ZahlFeld titel="Kaution (€)" wert={f.kaution} aendern={setze("kaution")} />
          <TextFeld titel="Mietbeginn" typ="date" wert={f.beginn} aendern={setze("beginn")} />
          <ZahlFeld titel="Personen im Haushalt" voll wert={f.personen} aendern={setze("personen")}
            platzhalter="Umlageschlüssel Nebenkosten" />
        </div>
        {kalt > 0 ? (
          <p className="hint">
            Warmmiete: {warm.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} / Monat
            {" · "}Jahreskaltmiete: {(kalt * 12).toLocaleString("de-DE",
              { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
          </p>
        ) : null}
      </Sheet>
    </form>
  );
}

// ------------------------------------------------------- Sanierungsposten

export function PostenBlatt(
  { objektId, posten, aufSchliessen }:
  { objektId: string; posten: Sanierungsposten | null; aufSchliessen: () => void },
) {
  const { laeuft, fuehreAus } = useSpeichern(aufSchliessen);
  const { aendern } = useZustand();
  const [f, setzeF] = useState({
    gewerk: posten?.gewerk ?? "",
    budget: String(posten?.budget ?? ""),
    ist: String(posten?.ist ?? ""),
    status: posten?.status ?? "offen",
    faellig: alsFeldDatum(posten?.faellig),
    notiz: posten?.notiz ?? "",
  });
  const setze = <K extends keyof typeof f>(feld: K) => (w: typeof f[K]) => setzeF({ ...f, [feld]: w });

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!f.gewerk.trim()) return;
      const daten = {
        gewerk: f.gewerk.trim(), budget: zahl(f.budget), ist: zahl(f.ist),
        status: f.status, faellig: datumOderNull(f.faellig), notiz: f.notiz,
      };
      void fuehreAus(
        () => posten ? api.postenAendern(posten.id, daten) : api.postenAnlegen(objektId, daten),
        "Gespeichert");
    }}>
      <Sheet
        titel={posten ? "Sanierungsposten bearbeiten" : "Neuer Sanierungsposten"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {posten ? (
            <button className="btn btn-danger" type="button" onClick={() => {
              if (!confirm(`„${posten.gewerk}“ löschen?`)) return;
              void aendern(() => api.postenLoeschen(posten.id), "Gelöscht").then(aufSchliessen);
            }}>Löschen</button>
          ) : <span />}
          <AbbrechenSpeichern aufSchliessen={aufSchliessen} laeuft={laeuft} />
        </>}
      >
        <div className="form-grid">
          <TextFeld titel="Gewerk / Posten *" voll wert={f.gewerk} aendern={setze("gewerk")}
            platzhalter="z. B. Elektrik, Bad WE 1, Fenster" />
          <ZahlFeld titel="Budget (€)" wert={f.budget} aendern={setze("budget")} />
          <ZahlFeld titel="Ist-Kosten (€)" wert={f.ist} aendern={setze("ist")} />
          <WahlFeld titel="Status" wert={f.status} aendern={setze("status")}
            optionen={ausRecord(POSTEN_TEXT)} />
          <TextFeld titel="Fällig bis" typ="date" wert={f.faellig} aendern={setze("faellig")} />
          <TextFeld titel="Notiz" voll wert={f.notiz} aendern={setze("notiz")}
            platzhalter="Handwerker, Angebot …" />
        </div>
      </Sheet>
    </form>
  );
}

// ------------------------------------------------------------------ Mangel

export function MangelBlatt(
  { objekt, mangel, vorgabeEinheitId, aufSchliessen }:
  { objekt: Objekt; mangel: Mangel | null; vorgabeEinheitId?: string; aufSchliessen: () => void },
) {
  const { laeuft, fuehreAus } = useSpeichern(aufSchliessen);
  const { aendern, zeigeMeldung } = useZustand();
  // Bei einem neuen Mangel gibt es noch keine ID, an die ein Foto hängen
  // könnte. Die Auswahl wird deshalb vorgemerkt und direkt nach dem Anlegen
  // nachgeschoben — der Nutzer soll den Mangel nicht erst wieder aufrufen
  // müssen, nur um das Foto anzuhängen, das er gerade gemacht hat.
  const [vorgemerkt, setzeVorgemerkt] = useState<Vorgemerkt | null>(null);
  const [f, setzeF] = useState({
    einheit_id: mangel?.einheit_id ?? vorgabeEinheitId ?? "",
    raum: mangel?.raum ?? "",
    art: mangel?.art ?? "Sonstiges",
    beschreibung: mangel?.beschreibung ?? "",
    prio: mangel?.prio ?? "mittel",
    kosten: String(mangel?.kosten ?? ""),
    kostentraeger: mangel?.kostentraeger ?? "Eigentümer",
    investiv: mangel?.investiv ?? true,
    beheber: mangel?.beheber ?? "",
    faellig: alsFeldDatum(mangel?.faellig),
    erledigt: mangel?.erledigt ?? false,
  });
  const setze = <K extends keyof typeof f>(feld: K) => (w: typeof f[K]) => setzeF({ ...f, [feld]: w });

  /** Kostenträger ändern belegt „investiv“ neu vor — was ein Dritter zahlt,
   *  ist keine eigene Anschaffung. Von Hand umgestellt bleibt es aber so. */
  const setzeKostentraeger = (w: string) =>
    setzeF({ ...f, kostentraeger: w, investiv: w === "Eigentümer" });

  const raumVorschlaege = [...new Set([
    ...RAEUME,
    ...objekt.einheiten.flatMap((e) => e.inventar.map((i) => i.raum)),
    ...objekt.maengel.map((m) => m.raum),
  ].filter(Boolean))];

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!f.beschreibung.trim()) return;
      const daten = {
        einheit_id: f.einheit_id || null, raum: f.raum, art: f.art,
        beschreibung: f.beschreibung.trim(), prio: f.prio, kosten: zahl(f.kosten),
        investiv: f.investiv, kostentraeger: f.kostentraeger, beheber: f.beheber,
        faellig: datumOderNull(f.faellig), erledigt: f.erledigt,
      };
      void fuehreAus(async () => {
        if (mangel) return api.mangelAendern(mangel.id, daten);
        const angelegt = await api.mangelAnlegen(objekt.id, daten);
        if (vorgemerkt && angelegt.neueId) {
          try {
            return await api.dateiHochladen(
              vorgemerkt.blob, vorgemerkt.name, "mangel", angelegt.neueId, "foto");
          } catch {
            // Der Mangel ist angelegt — nur das Foto fehlt. Das ist eine
            // Teilmeldung wert, kein Rückgängigmachen.
            zeigeMeldung("Mangel angelegt, aber das Foto ließ sich nicht anhängen.", true);
          }
        }
        return angelegt;
      }, mangel ? "Gespeichert" : "Mangel angelegt");
    }}>
      <Sheet
        titel={mangel ? "Mangel bearbeiten" : "Neuer Mangel"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {mangel ? (
            <button className="btn btn-danger" type="button" onClick={() => {
              if (!confirm("Diesen Mangel löschen?")) return;
              void aendern(() => api.mangelLoeschen(mangel.id), "Gelöscht").then(aufSchliessen);
            }}>Löschen</button>
          ) : <span />}
          <AbbrechenSpeichern aufSchliessen={aufSchliessen} laeuft={laeuft} />
        </>}
      >
        <div className="form-grid">
          <WahlFeld titel="Mieteinheit" wert={f.einheit_id} aendern={setze("einheit_id")}
            optionen={[["", "Allgemein / Haus"] as const,
              ...objekt.einheiten.map((e) => [e.id, e.bezeichnung] as const)]} />
          <TextFeld titel="Raum" wert={f.raum} aendern={setze("raum")}
            platzhalter="z. B. Bad" liste={raumVorschlaege} />
          <TextFeld titel="Beschreibung *" voll wert={f.beschreibung} aendern={setze("beschreibung")}
            platzhalter="Was genau ist der Mangel?" />
          <WahlFeld titel="Art des Mangels" wert={f.art} aendern={setze("art")}
            optionen={ausListe(MANGEL_ARTEN)} />
          <WahlFeld titel="Priorität" wert={f.prio} aendern={setze("prio")}
            optionen={ausRecord(PRIO_TEXT)} />
          <ZahlFeld titel="Kosten (€)" wert={f.kosten} aendern={setze("kosten")}
            platzhalter="geschätzt oder tatsächlich" />
          <WahlFeld titel="Kostenträger" wert={f.kostentraeger} aendern={setzeKostentraeger}
            optionen={ausListe(KOSTENTRAEGER)} />
          <TextFeld titel="Mängelbeheber" wert={f.beheber} aendern={setze("beheber")}
            platzhalter="Firma oder Person" />
          <TextFeld titel="Fällig bis" typ="date" wert={f.faellig} aendern={setze("faellig")} />
          <HakenFeld titel="Zählt zur Gesamtinvestition"
            wert={f.investiv} aendern={setze("investiv")} />
          <HakenFeld titel="Erledigt" wert={f.erledigt} aendern={setze("erledigt")} />
          <AnhangFeld entitaet="mangel" entitaetId={mangel?.id}
            vorhandene={mangel?.dateien ?? []}
            vorgemerkt={vorgemerkt} aufVormerken={setzeVorgemerkt} />
        </div>
        <p className="hint">
          „Zählt zur Gesamtinvestition“ ist aus dem Kostenträger vorbelegt. Abwählen,
          wenn dieselbe Arbeit schon als Sanierungsposten budgetiert ist — sonst
          steht sie zweimal in der Summe.
        </p>
      </Sheet>
    </form>
  );
}

// ---------------------------------------------------------------- Inventar

export function InventarBlatt(
  { einheitId, position, vorgabeRaum, raumVorschlaege, aufSchliessen }:
  {
    einheitId: string; position: InventarPosition | null; vorgabeRaum?: string;
    raumVorschlaege: string[]; aufSchliessen: () => void;
  },
) {
  const { laeuft, fuehreAus } = useSpeichern(aufSchliessen);
  const { aendern } = useZustand();
  const [f, setzeF] = useState({
    raum: position?.raum ?? vorgabeRaum ?? "Küche",
    bezeichnung: position?.bezeichnung ?? "",
    pflicht: position?.pflicht ?? "notwendig",
    vorhanden: position?.vorhanden ?? "unklar",
    zustand: position?.zustand ?? "",
    anmerkung: position?.anmerkung ?? "",
    neu: position?.neu ?? "",
    kosten: String(position?.kosten ?? ""),
  });
  const setze = <K extends keyof typeof f>(feld: K) => (w: typeof f[K]) => setzeF({ ...f, [feld]: w });

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!f.bezeichnung.trim() || !f.raum.trim()) return;
      const daten = {
        raum: f.raum.trim(), bezeichnung: f.bezeichnung.trim(), pflicht: f.pflicht,
        vorhanden: f.vorhanden, zustand: f.zustand, anmerkung: f.anmerkung,
        neu: f.neu, kosten: zahl(f.kosten),
      };
      void fuehreAus(
        () => position ? api.inventarAendern(position.id, daten) : api.inventarAnlegen(einheitId, daten),
        "Gespeichert");
    }}>
      <Sheet
        titel={position ? "Inventarposition bearbeiten" : "Neue Inventarposition"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {position ? (
            <button className="btn btn-danger" type="button" onClick={() => {
              if (!confirm(`„${position.bezeichnung}“ löschen?`)) return;
              void aendern(() => api.inventarLoeschen(position.id), "Gelöscht").then(aufSchliessen);
            }}>Löschen</button>
          ) : <span />}
          <AbbrechenSpeichern aufSchliessen={aufSchliessen} laeuft={laeuft} />
        </>}
      >
        <div className="form-grid">
          <TextFeld titel="Raum *" wert={f.raum} aendern={setze("raum")}
            platzhalter="z. B. Küche" liste={raumVorschlaege} />
          <WahlFeld titel="Bedarf" wert={f.pflicht} aendern={setze("pflicht")}
            optionen={[["notwendig", "notwendig"], ["optional", "optional"]] as const} />
          <TextFeld titel="Position *" voll wert={f.bezeichnung} aendern={setze("bezeichnung")}
            platzhalter="z. B. Kühlschrank mit Gefrierkombination" />
          <WahlFeld titel="Bestand" wert={f.vorhanden} aendern={setze("vorhanden")}
            optionen={ausRecord(BESTAND_TEXT)} />
          <WahlFeld titel="Zustand" wert={f.zustand} aendern={setze("zustand")}
            optionen={ausRecord(ZUSTAND_TEXT)} />
          <TextFeld titel="Anmerkung" voll wert={f.anmerkung} aendern={setze("anmerkung")}
            platzhalter="z. B. zerkratzt, Front beschädigt" />
          <WahlFeld titel="Neu beschaffen?" wert={f.neu} aendern={setze("neu")}
            optionen={ausRecord(NEU_TEXT)} />
          <ZahlFeld titel="Neu: Kosten (€)" wert={f.kosten} aendern={setze("kosten")} />
        </div>
      </Sheet>
    </form>
  );
}

// ----------------------------------------------------------------- Vorlage

export function VorlageBlatt(
  { position, raumVorschlaege, aufSchliessen }:
  { position: VorlagePosition | null; raumVorschlaege: string[]; aufSchliessen: () => void },
) {
  const { laeuft, fuehreAus } = useSpeichern(aufSchliessen);
  const { aendern } = useZustand();
  const [f, setzeF] = useState({
    raum: position?.raum ?? "Küche",
    bezeichnung: position?.bezeichnung ?? "",
    pflicht: position?.pflicht ?? "notwendig",
  });
  const setze = <K extends keyof typeof f>(feld: K) => (w: typeof f[K]) => setzeF({ ...f, [feld]: w });

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!f.bezeichnung.trim() || !f.raum.trim()) return;
      const daten = { raum: f.raum.trim(), bezeichnung: f.bezeichnung.trim(), pflicht: f.pflicht };
      void fuehreAus(
        () => position ? api.vorlageAendern(position.id, daten) : api.vorlageAnlegen(daten),
        "Gespeichert");
    }}>
      <Sheet
        titel={position ? "Vorlagenposition bearbeiten" : "Neue Vorlagenposition"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {position ? (
            <button className="btn btn-danger" type="button" onClick={() => {
              if (!confirm(`„${position.bezeichnung}“ aus der Vorlage entfernen?`)) return;
              void aendern(() => api.vorlageLoeschen(position.id), "Entfernt").then(aufSchliessen);
            }}>Löschen</button>
          ) : <span />}
          <AbbrechenSpeichern aufSchliessen={aufSchliessen} laeuft={laeuft} />
        </>}
      >
        <div className="form-grid">
          <TextFeld titel="Raum *" wert={f.raum} aendern={setze("raum")} liste={raumVorschlaege} />
          <WahlFeld titel="Bedarf" wert={f.pflicht} aendern={setze("pflicht")}
            optionen={[["notwendig", "notwendig"], ["optional", "optional"]] as const} />
          <TextFeld titel="Position *" voll wert={f.bezeichnung} aendern={setze("bezeichnung")} />
        </div>
        <p className="hint">
          Änderungen wirken nur auf künftige Anwendungen — bereits erfasste
          Inventare bleiben unberührt.
        </p>
      </Sheet>
    </form>
  );
}

// -------------------------------------------------------- Vorlage anwenden

export function VorlageAnwendenBlatt(
  { einheitId, raeume, anzahlJeRaum, aufSchliessen }:
  {
    einheitId: string | null; raeume: string[];
    anzahlJeRaum: Record<string, number>; aufSchliessen: () => void;
  },
) {
  const { aendern, zeigeMeldung } = useZustand();
  const [gewaehlt, setzeGewaehlt] = useState<string[]>(raeume);
  const [laeuft, setzeLaeuft] = useState(false);

  return (
    <Sheet
      titel="Vorlage anwenden"
      aufSchliessen={aufSchliessen}
      fuss={
        <div className="r">
          <button className="btn btn-quiet" onClick={aufSchliessen}>Abbrechen</button>
          <button className="btn btn-primary" disabled={laeuft || gewaehlt.length === 0}
            onClick={async () => {
              setzeLaeuft(true);
              let bericht = "";
              const ok = await aendern(async () => {
                const ergebnis = await api.vorlageAnwenden(einheitId, gewaehlt);
                const n = ergebnis.bericht?.ergaenzt ?? 0;
                const e = ergebnis.bericht?.betroffeneEinheiten ?? 0;
                bericht = n === 0
                  ? "Nichts zu ergänzen — alles war schon da."
                  : `${n} ${n === 1 ? "Position" : "Positionen"} in ${e} ${e === 1 ? "Einheit" : "Einheiten"} ergänzt.`;
                return ergebnis;
              });
              if (ok && bericht) zeigeMeldung(bericht);
              setzeLaeuft(false);
              if (ok) aufSchliessen();
            }}>
            {laeuft ? "Wendet an …" : "Anwenden"}
          </button>
        </div>
      }
    >
      <p className="sub" style={{ margin: "0 0 14px" }}>
        Räume auswählen, die auf {einheitId ? "diese Einheit" : "alle Einheiten"} übertragen
        werden. Bereits vorhandene Positionen werden nicht doppelt angelegt und nicht
        überschrieben.
      </p>
      <div className="form-grid">
        {raeume.map((r) => (
          <label className="check" key={r} style={{ gridColumn: "auto" }}>
            <input type="checkbox" checked={gewaehlt.includes(r)}
              onChange={(e) => setzeGewaehlt(e.target.checked
                ? [...gewaehlt, r]
                : gewaehlt.filter((x) => x !== r))} />
            {r} <span className="sub" style={{ margin: 0 }}>({anzahlJeRaum[r] ?? 0})</span>
          </label>
        ))}
      </div>
    </Sheet>
  );
}

// -------------------------------------------------- Vorlage aus einer Einheit

export function VorlageAusEinheitBlatt(
  { einheiten, aufSchliessen }:
  {
    einheiten: { objekt: { id: string; name: string }; einheit: Einheit }[];
    aufSchliessen: () => void;
  },
) {
  const { aendern, zeigeMeldung } = useZustand();
  const mitInventar = einheiten.filter(({ einheit }) => einheit.inventar.length > 0);
  const [quelle, setzeQuelle] = useState(mitInventar[0]?.einheit.id ?? "");
  const [umfang, setzeUmfang] = useState<"notwendig" | "alle">("notwendig");
  const [laeuft, setzeLaeuft] = useState(false);

  if (mitInventar.length === 0) {
    return (
      <Sheet titel="Aus Einheit übernehmen" aufSchliessen={aufSchliessen}
        fuss={<div className="r">
          <button className="btn btn-quiet" onClick={aufSchliessen}>Schließen</button>
        </div>}>
        <p>Keine Einheit hat bisher ein erfasstes Inventar.</p>
      </Sheet>
    );
  }

  return (
    <Sheet
      titel="Vorlage aus Einheit übernehmen"
      aufSchliessen={aufSchliessen}
      fuss={<div className="r">
        <button className="btn btn-quiet" onClick={aufSchliessen}>Abbrechen</button>
        <button className="btn btn-primary" disabled={laeuft || !quelle} onClick={async () => {
          setzeLaeuft(true);
          let anzahl = 0;
          const ok = await aendern(async () => {
            const ergebnis = await api.vorlageAusEinheit(quelle, umfang);
            anzahl = ergebnis.bericht?.uebernommen ?? 0;
            return ergebnis;
          });
          setzeLaeuft(false);
          if (ok) { zeigeMeldung(`Vorlage ersetzt: ${anzahl} Positionen.`); aufSchliessen(); }
        }}>{laeuft ? "Übernimmt …" : "Übernehmen"}</button>
      </div>}
    >
      <p className="sub" style={{ margin: "0 0 14px" }}>
        Die Vorlage wird durch die Positionen der gewählten Einheit <b>ersetzt</b>.
        Bestand, Zustand, Anmerkungen und Kosten wandern nicht mit — eine Vorlage
        sagt, was da sein soll, nicht wie es aussieht.
      </p>
      <div className="form-grid">
        <WahlFeld titel="Einheit" voll wert={quelle} aendern={setzeQuelle}
          optionen={mitInventar.map(({ objekt, einheit }) =>
            [einheit.id, `${objekt.name} — ${einheit.bezeichnung} (${einheit.inventar.length})`] as const)} />
        <WahlFeld titel="Umfang" voll wert={umfang} aendern={setzeUmfang}
          optionen={[
            ["notwendig", "nur notwendige Positionen"],
            ["alle", "alle Positionen (inkl. optional)"],
          ] as const} />
      </div>
    </Sheet>
  );
}
